/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.25.1
 */

import { ChangeEvent, ReactNode, useCallback, useRef } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import { useFormikContext } from 'formik';

import { icons } from '@/main/assets';
import AttachedItem from '@/main/components/common/AttachedItem';
import { ImgStyled } from '@/main/components/common/Block/Img';
import { EInputKind } from '@/main/components/common/Input/Input';
import { LabelHintStyled, LabelMarkStyled, LabelStyled } from '@/main/components/common/Input/styled/InputStyled';
import { fonts } from '@/main/theme';

import FormField, { IFormFieldProps } from '../FormField';
import {
    InputContainerFileStyled,
    InputFileItemContainerStyled,
    InputFileLabelStyled,
    InputFileStyled,
    InputWrapperFileStyled,
} from './FormFileInputStyled';

interface IFormFileInput {
    children?: ReactNode;
    attachText: string;
    error?: string;
}

//  * Этот инпут предназначен для загрузки 1 файла, если необходима мультизагрзука - требуется доработать.
const FormFileInput = (props: IFormFieldProps & IFormFileInput) => {
    const { setFieldValue } = useFormikContext();

    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleClear = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    }, []);
    return (
        <FormField hideValidation {...props}>
            {({ field, kind, disabled, label, labelHint, dataName, required }) => {
                const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
                    const inputFilesList = ev.target.files;
                    if (inputFilesList && inputFilesList.length >= 1) {
                        setFieldValue(field.name, inputFilesList[0]);
                    }
                };

                const handleDelete = () => {
                    setFieldValue(field.name, null);
                };

                if (!field.value) {
                    handleClear();
                }

                return (
                    <InputContainerFileStyled>
                        {label && (
                            <LabelStyled kind={kind ? kind : EInputKind.normal} labelFont={fonts.textMainBold}>
                                {label}
                                {required && <LabelMarkStyled className={'input__label-mark'}>*</LabelMarkStyled>}
                            </LabelStyled>
                        )}
                        {labelHint && <LabelHintStyled>{labelHint}</LabelHintStyled>}
                        <InputWrapperFileStyled>
                            {props.children}
                            <InputFileLabelStyled>
                                <ImgStyled className="small" src={icons.attachFileIcon} />
                                <Typography component="p" variant="h5">
                                    {props.attachText}
                                </Typography>
                                <InputFileStyled
                                    data-name={dataName}
                                    disabled={disabled}
                                    multiple={false}
                                    ref={inputRef}
                                    type="file"
                                    onChange={handleChange}
                                />
                            </InputFileLabelStyled>
                        </InputWrapperFileStyled>
                        {!!field.value && (
                            <InputFileItemContainerStyled>
                                <AttachedItem
                                    fullWidth={false}
                                    title={(field.value as File).name}
                                    onDelete={handleDelete}
                                />
                            </InputFileItemContainerStyled>
                        )}
                        {!!props.error && <Typography color="error">{props.error}</Typography>}
                    </InputContainerFileStyled>
                );
            }}
        </FormField>
    );
};

export default FormFileInput;
